import moment from 'moment-timezone';
import { LANGUAGE_CODE } from './intl';
import { TIMEZONE } from './formatTime';
import logger from './logger';

const log = logger.createLogger('dateTime');

export const formatDate = (date, format = 'MMMM D, YYYY') =>
  date ? moment(date).format(format) : date;

export const formatDateTime = (date, format = 'MMMM D, YYYY, h:mm A') =>
  date ? moment(date).format(format) : date;

export const formatDateTimeForAPI = (date) =>
  date ? moment(date).utc().format() : date;

export const formatDateTimeConversational = (date) =>
  date ? moment(date).fromNow() : date;

/**
 * Get the first date of the same month with the input date
 * @param {Date} date
 */

export const getFirstDateOfTheSameMonth = (date) => {
  const firstDate = new Date(date.getFullYear(), date.getMonth(), 1);

  // Set the time to 0
  firstDate.setHours(0, 0, 0, 0);

  return firstDate;
};

export const getLastDateOfTheSameMonth = (date) => {
  const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  lastDate.setHours(23, 59, 59, 999);
  return lastDate;
};

/**
 *  Get the date object of first day of previous month relative to the specified current date;
 *  @param {Date} currentDate current date object
 *
 */
export const getFirstDayPreviousMonth = (currentDate) => {
  const firstDayCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const firstDayPreviousMonth = new Date(
    firstDayCurrentMonth.getFullYear(),
    firstDayCurrentMonth.getMonth() - 1,
    1
  );
  // Setting the time to the moment of 00:00:00;
  firstDayPreviousMonth.setHours(0, 0, 0, 0);

  return firstDayPreviousMonth;
};

/**
 *  Get the date object of 7 days ago relative to the specified current date;
 *  @param {Date} currentDate current date object
 *
 */
export const getSevenDaysAgo = (currentDate) => {
  const sevenDaysAgo = new Date(currentDate);
  sevenDaysAgo.setDate(currentDate.getDate() - 7);
  // Setting the time to the moment of 00:00:00;
  sevenDaysAgo.setHours(0, 0, 0, 0);

  return sevenDaysAgo;
};

/**
 * Converts a UTC date string to a readable date format.
 *
 * @param {string} dateString - The UTC date string to be converted, e.g., '2024-01-10T10:29:17.054Z'.
 * @returns {string} The date formatted in a readable Japanese format, e.g., '2024年01月10日 19時29分17秒'.
 */
export const readableDateTimeFormat = (dateString, lang, timeZone) => {
  const effectiveTimeZone = isNullOrEmpty(timeZone) ? TIMEZONE.JAPAN : timeZone;

  if (isNullOrEmpty(dateString)) {
    log.warn('dateString is falsy:', dateString);
    return '';
  }

  switch (lang) {
    case LANGUAGE_CODE.JAPANESE:
    case LANGUAGE_CODE.CHINESE: {
      const dateInTokyo = moment(dateString).tz(effectiveTimeZone);
      return dateInTokyo.format('YYYY年MM月DD日 HH時mm分');
    }
    default:
      return formatDateTimeConversational(dateString);
  }
};

/**
 *  Converts a UTC date string to a readable Japanese date format.
 *  Only show the year in YYYY if the year is the same as the current year.
 *
 * @param {string} dateString - The UTC date string to be converted, e.g., '2024-01-10T10:29:17.054Z'.
 * @returns {string} The date formatted in a readable Japanese format, e.g., '2024年01月10日 19時29分17秒'.
 */
export const readableDateFormat = (dateString, timeZone) => {
  const currentYear = new Date().getFullYear();
  const effectiveTimeZone = timeZone == 'null' ? TIMEZONE.JAPAN : timeZone;
  let dateTime;
  let formatString;
  switch (timeZone) {
    case TIMEZONE.JAPAN:
    case TIMEZONE.CHINA:
      dateTime = moment.tz(dateString, effectiveTimeZone);
      formatString =
        dateTime.year() === currentYear
          ? 'MM月DD日 HH時mm分'
          : 'YYYY年MM月DD日 HH時mm分';
      break;
    default:
      dateTime = moment(dateString);
      formatString =
        dateTime.year() === currentYear ? 'MM-DD HH:mm' : 'YYYY-MM-DD HH:mm';
      break;
  }

  return dateTime.format(formatString);
};

/**
 * Checks if a value is null, undefined, empty string, or the string 'null'/'undefined'
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value is considered empty/null/undefined
 */
const isNullOrEmpty = (value) => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    value === 'null' ||
    value === 'undefined' ||
    (typeof value === 'string' && value.trim() === '')
  );
};
